import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Trophy } from 'lucide-react';
import { apiService } from '../services/api';
import { Loading, ErrorMessage, Card } from '../components/SharedComponents';

function Matches() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      try {
        const filesResponse = await apiService.getAllMatchFiles();
        if (filesResponse.data && filesResponse.data.length > 0) {
          setMatches(filesResponse.data);
        } else {
          const datasetResponse = await apiService.getDatasetMatches();
          let matchesData = [];
          if (datasetResponse.data) {
            if (Array.isArray(datasetResponse.data)) {
              matchesData = datasetResponse.data;
            } else if (datasetResponse.data.response && Array.isArray(datasetResponse.data.response)) {
              matchesData = datasetResponse.data.response;
            } else if (datasetResponse.data.items && Array.isArray(datasetResponse.data.items)) {
              matchesData = datasetResponse.data.items;
            }
          } else if (Array.isArray(datasetResponse)) {
            matchesData = datasetResponse;
          }

          if (matchesData.length > 0) {
            setMatches(matchesData);
          } else {
            const response = await apiService.getMatches({ limit: 50 });
            setMatches(response.data);
          }
        }
      } catch (err) {
        console.error('Error fetching match files:', err);
        const response = await apiService.getMatches({ limit: 50 });
        setMatches(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading message="Loading matches..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchMatches} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: '32px',
            fontWeight: '700',
            color: '#1c1917',
            letterSpacing: '-0.5px'
          }}>IPL Matches</h1>
          <p style={{ color: '#57534e', marginTop: '6px', fontSize: '15px' }}>View all IPL match results</p>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          backgroundColor: '#fafaf9',
          borderRadius: '10px',
          border: '1px solid #f5f5f4'
        }}>
          <Calendar style={{ width: '18px', height: '18px', color: '#e85d04' }} />
          <span style={{ fontWeight: '600', fontSize: '14px', color: '#1c1917' }}>{matches.length} Matches</span>
        </div>
      </div>

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Date</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Match</th>
                <th className="hide-mobile" style={tableHeaderStyle}>Venue</th>
                <th style={tableHeaderStyle}>Winner</th>
                <th className="hide-mobile" style={tableHeaderStyle}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((match, index) => (
                <tr
                  key={match.match_id || match.id || index}
                  style={{
                    transition: 'background-color 0.15s',
                    cursor: (match.filename || match.match_id) ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (match.filename) {
                      navigate(`/matches/${match.filename}`);
                    } else if (match.match_id) {
                      const teamA = match.teama?.name?.replace(/\s+/g, '_') || 'TeamA';
                      const teamB = match.teamb?.name?.replace(/\s+/g, '_') || 'TeamB';
                      const matchNum = match.subtitle?.replace(/\s+/g, '_') || `Match_${match.match_number || match.match_id}`;
                      navigate(`/matches/${teamA}_vs_${teamB}_${matchNum}`);
                    } else if (match.id) {
                      navigate(`/matches/${match.id}`);
                    }
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafaf9'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{ ...tableCellStyle, color: '#57534e', whiteSpace: 'nowrap', fontSize: '13px' }}>
                    {match.date_start ? new Date(match.date_start).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    }) : match.date ? new Date(match.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    }) : match.matchDate ? new Date(match.matchDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    }) : 'N/A'}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        padding: '4px 10px',
                        background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {match.teama?.short_name || match.teams?.teamA?.short_name || match.team1?.shortName || match.teamA?.short_name || 'TBD'}
                      </span>
                      <span style={{ color: '#a8a29e', fontSize: '12px', fontWeight: '500' }}>vs</span>
                      <span style={{
                        padding: '4px 10px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {match.teamb?.short_name || match.teams?.teamB?.short_name || match.team2?.shortName || match.teamB?.short_name || 'TBD'}
                      </span>
                    </div>
                  </td>
                  <td className="hide-mobile" style={{ ...tableCellStyle, color: '#57534e' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#1c1917', fontSize: '13px' }}>
                        {match.venue?.name || (typeof match.venue === 'string' ? match.venue : 'N/A')}
                      </div>
                      <div style={{ fontSize: '11px', color: '#a8a29e' }}>
                        {match.venue?.location || match.city || ''}
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <Trophy style={{ width: '14px', height: '14px', color: '#f59e0b' }} />
                      <span style={{ fontWeight: '600', color: '#22c55e', fontSize: '13px' }}>
                        {(() => {
                          if (match.winning_team_id) {
                            if (match.winning_team_id === match.teama?.team_id) return match.teama?.short_name;
                            if (match.winning_team_id === match.teamb?.team_id) return match.teamb?.short_name;
                          }
                          if (match.status_note) {
                            const winnerMatch = match.status_note.match(/(.+?)\s+won by/);
                            if (winnerMatch) {
                              if (match.teama?.name && match.status_note.includes(match.teama.name)) return match.teama.short_name;
                              if (match.teamb?.name && match.status_note.includes(match.teamb.name)) return match.teamb.short_name;
                            }
                          }
                          return match.winner?.short_name || match.winner?.shortName || match.winning_team?.name || 'TBD';
                        })()}
                      </span>
                    </div>
                  </td>
                  <td className="hide-mobile" style={{ ...tableCellStyle, color: '#57534e', fontSize: '13px' }}>
                    {(() => {
                      if (match.status_note) {
                        const runsMatch = match.status_note.match(/won by (\d+)\s+runs?/i);
                        const wicketsMatch = match.status_note.match(/won by (\d+)\s+wickets?/i);
                        if (runsMatch) return `${runsMatch[1]} runs`;
                        if (wicketsMatch) return `${wicketsMatch[1]} wkts`;
                      }
                      return match.win_margin || (match.winByRuns > 0 && `${match.winByRuns} runs`) || (match.winByWickets > 0 && `${match.winByWickets} wkts`) || '-';
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {matches.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#a8a29e' }}>
            <Calendar style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.5 }} />
            <p style={{ fontSize: '15px', fontWeight: '500' }}>No matches found</p>
          </div>
        )}
      </Card>
    </div>
  );
}

const tableHeaderStyle = {
  padding: '12px 14px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '12px',
  color: '#57534e',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid #e7e5e4',
  backgroundColor: '#fafaf9'
};

const tableCellStyle = {
  padding: '14px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#1c1917',
  borderBottom: '1px solid #f5f5f4'
};

export default Matches;
